/**
 * Hook useCalendarioChiusure
 * Il Diario di Bordo - SaaS
 * 
 * Logica per determinare se un giorno è aperto/chiuso
 * Usato da CalendarView, NewEntryPage, Dashboard
 * 
 * TIPOLOGIE STATO GIORNO:
 * - normale: giorno lavorativo standard
 * - weekend: giorno di chiusura settimanale
 * - festivita: festività italiana (chiuso)
 * - festivita_lavorata: festività ma centro aperto
 * - santo_patrono: santo patrono locale (chiuso)
 * - patrono_lavorativo: santo patrono ma centro aperto
 * - chiusura_straordinaria: chiusura extra
 * - apertura_straordinaria: apertura in giorno normalmente chiuso
 */

import { useMemo, useCallback } from 'react'

/**
 * Festività fisse italiane
 */
export const FESTIVITA_FISSE = [
  { codice: 'CAPODANNO', nome: 'Capodanno', giorno: 1, mese: 1, emoji: '🎆' },
  { codice: 'EPIFANIA', nome: 'Epifania', giorno: 6, mese: 1, emoji: '👑' },
  { codice: 'LIBERAZIONE', nome: 'Festa della Liberazione', giorno: 25, mese: 4, emoji: '🇮🇹' },
  { codice: 'LAVORO', nome: 'Festa dei Lavoratori', giorno: 1, mese: 5, emoji: '⚒️' },
  { codice: 'REPUBBLICA', nome: 'Festa della Repubblica', giorno: 2, mese: 6, emoji: '🇮🇹' },
  { codice: 'FERRAGOSTO', nome: 'Ferragosto', giorno: 15, mese: 8, emoji: '☀️' },
  { codice: 'SAN_FRANCESCO', nome: 'San Francesco (Patrono d\'Italia)', giorno: 4, mese: 10, emoji: '🕊️' },
  { codice: 'OGNISSANTI', nome: 'Tutti i Santi', giorno: 1, mese: 11, emoji: '👼' },
  { codice: 'IMMACOLATA', nome: 'Immacolata Concezione', giorno: 8, mese: 12, emoji: '🙏' },
  { codice: 'NATALE', nome: 'Natale', giorno: 25, mese: 12, emoji: '🎄' },
  { codice: 'SANTO_STEFANO', nome: 'Santo Stefano', giorno: 26, mese: 12, emoji: '🎁' }
]

/**
 * Calcola la data della Pasqua per un anno dato
 * Algoritmo di Gauss/Meeus
 */
export function calcolaPasqua(year) {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const mese = Math.floor((h + l - 7 * m + 114) / 31)
  const giorno = ((h + l - 7 * m + 114) % 31) + 1
  
  return new Date(year, mese - 1, giorno)
}

/**
 * Ottiene tutte le festività per un anno specifico
 */
export function getFestivitaAnno(year) {
  const festivita = []
  
  // Aggiungi festività fisse
  FESTIVITA_FISSE.forEach(f => {
    festivita.push({
      ...f,
      data: new Date(year, f.mese - 1, f.giorno),
      tipo: 'fissa'
    })
  })
  
  // Calcola Pasqua e Pasquetta
  const pasqua = calcolaPasqua(year)
  festivita.push({
    codice: 'PASQUA',
    nome: 'Pasqua',
    giorno: pasqua.getDate(),
    mese: pasqua.getMonth() + 1,
    emoji: '🐣',
    data: pasqua,
    tipo: 'mobile'
  })
  
  const pasquetta = new Date(pasqua)
  pasquetta.setDate(pasquetta.getDate() + 1)
  festivita.push({
    codice: 'PASQUETTA',
    nome: 'Lunedì dell\'Angelo',
    giorno: pasquetta.getDate(),
    mese: pasquetta.getMonth() + 1,
    emoji: '🐰',
    data: pasquetta,
    tipo: 'mobile'
  })
  
  // Ordina per data
  festivita.sort((a, b) => a.data - b.data)
  
  return festivita
}

/**
 * Determina lo stato di un giorno specifico
 * Priorità: aperture straordinarie > chiusure > festività > patrono > weekend
 * 
 * @param {Date|string} data - Data da verificare
 * @param {Object} settings - Impostazioni calendario tenant (settings.calendario)
 * @returns {Object} Stato del giorno
 */
export function getStatoGiorno(data, settings = {}) {
  const d = new Date(data)
  d.setHours(0, 0, 0, 0)
  
  const giorniApertura = settings.giorniApertura || ['lunedi', 'martedi', 'mercoledi', 'giovedi', 'venerdi']
  const giorniSettimana = ['domenica', 'lunedi', 'martedi', 'mercoledi', 'giovedi', 'venerdi', 'sabato']
  const giornoSettimana = giorniSettimana[d.getDay()]
  
  // 1. CHECK APERTURE STRAORDINARIE (priorità massima)
  const apertureEccezioni = settings.apertureEccezioni || []
  const aperturaStrao = apertureEccezioni.find(a => {
    const ad = new Date(a.data)
    ad.setHours(0, 0, 0, 0)
    return ad.getTime() === d.getTime()
  })
  
  if (aperturaStrao) {
    // Verifica se è un giorno che sarebbe normalmente chiuso
    const sarebbeFestivita = checkFestivita(d, settings)
    const sarebbePatrono = checkPatrono(d, settings)
    const sarebbeWeekend = !giorniApertura.includes(giornoSettimana)
    
    return {
      aperto: true,
      tipologia: 'apertura_straordinaria',
      motivo: aperturaStrao.motivo || 'Apertura straordinaria',
      emoji: '⭐',
      orarioApertura: aperturaStrao.orarioApertura || settings.orarioApertura,
      orarioChiusura: aperturaStrao.orarioChiusura || settings.orarioChiusura,
      // Info aggiuntive per indicare cosa è stato "sovrascritto"
      motivoOriginale: sarebbeFestivita?.nome || sarebbePatrono?.nome || (sarebbeWeekend ? 'Weekend' : null),
      emojiOriginale: sarebbeFestivita?.emoji || sarebbePatrono?.emoji || (sarebbeWeekend ? '📅' : null)
    }
  }
  
  // 2. CHECK CHIUSURE STRAORDINARIE
  const chiusure = settings.chiusure || []
  for (const chiusura of chiusure) {
    const dataInizio = new Date(chiusura.dataInizio)
    dataInizio.setHours(0, 0, 0, 0)
    
    let dataFine = chiusura.dataFine ? new Date(chiusura.dataFine) : new Date(dataInizio)
    dataFine.setHours(0, 0, 0, 0)
    
    // Gestione ricorrente
    if (chiusura.ricorrente) {
      const yearDiff = d.getFullYear() - dataInizio.getFullYear()
      const checkInizio = new Date(dataInizio)
      checkInizio.setFullYear(checkInizio.getFullYear() + yearDiff)
      const checkFine = new Date(dataFine)
      checkFine.setFullYear(checkFine.getFullYear() + yearDiff)
      
      if (d >= checkInizio && d <= checkFine) {
        return {
          aperto: chiusura.tipologia !== 'intera',
          tipologia: 'chiusura_straordinaria',
          motivo: chiusura.motivo,
          emoji: '🚫',
          parziale: chiusura.tipologia !== 'intera' ? chiusura.tipologia : null,
          ricorrente: true
        }
      }
    } else {
      if (d >= dataInizio && d <= dataFine) {
        return {
          aperto: chiusura.tipologia !== 'intera',
          tipologia: 'chiusura_straordinaria',
          motivo: chiusura.motivo,
          emoji: '🚫',
          parziale: chiusura.tipologia !== 'intera' ? chiusura.tipologia : null
        }
      }
    }
  }
  
  // 3. CHECK FESTIVITÀ ITALIANE
  if (settings.festivitaItaliane?.enabled !== false) {
    const festivitaInfo = checkFestivita(d, settings)
    
    if (festivitaInfo) {
      const lavorative = settings.festivitaItaliane?.lavorative || []
      const year = d.getFullYear()
      
      const isLavorativa = lavorative.some(l => 
        l.codice === festivitaInfo.codice && 
        (l.anno === null || l.anno === year)
      )
      
      if (isLavorativa) {
        // Festività ma centro aperto
        return {
          aperto: true,
          tipologia: 'festivita_lavorata',
          motivo: `${festivitaInfo.nome} (aperto)`,
          motivoOriginale: festivitaInfo.nome,
          emoji: '🎉',
          emojiOriginale: festivitaInfo.emoji,
          codice: festivitaInfo.codice,
          orarioApertura: settings.orarioApertura || '08:00',
          orarioChiusura: settings.orarioChiusura || '18:00'
        }
      } else {
        // Festività chiuso
        return {
          aperto: false,
          tipologia: 'festivita',
          motivo: festivitaInfo.nome,
          emoji: festivitaInfo.emoji || '🎉',
          codice: festivitaInfo.codice
        }
      }
    }
  }
  
  // 4. CHECK SANTO PATRONO
  const patrono = settings.santoPatrono
  if (patrono?.enabled && patrono.giorno && patrono.mese) {
    if (d.getDate() === patrono.giorno && d.getMonth() + 1 === patrono.mese) {
      if (patrono.lavorativo) {
        // Patrono ma centro aperto
        return {
          aperto: true,
          tipologia: 'patrono_lavorativo',
          motivo: `${patrono.nome || 'Santo Patrono'} (aperto)`,
          motivoOriginale: patrono.nome || 'Santo Patrono',
          emoji: '⛪',
          orarioApertura: settings.orarioApertura || '08:00',
          orarioChiusura: settings.orarioChiusura || '18:00'
        }
      } else {
        return {
          aperto: false,
          tipologia: 'santo_patrono',
          motivo: patrono.nome || 'Santo Patrono',
          emoji: '⛪'
        }
      }
    }
  }
  
  // 5. CHECK GIORNO SETTIMANALE
  if (!giorniApertura.includes(giornoSettimana)) {
    return {
      aperto: false,
      tipologia: 'weekend',
      motivo: 'Giorno di chiusura settimanale',
      emoji: '📅'
    }
  }
  
  // APERTO - giorno lavorativo normale
  return {
    aperto: true,
    tipologia: 'normale',
    motivo: null,
    emoji: null,
    orarioApertura: settings.orarioApertura || '08:00',
    orarioChiusura: settings.orarioChiusura || '18:00'
  }
}

/**
 * Helper: verifica se una data è una festività italiana
 */
function checkFestivita(d, settings) {
  if (settings.festivitaItaliane?.enabled === false) return null
  
  const year = d.getFullYear()
  const festivita = getFestivitaAnno(year)
  
  return festivita.find(f => 
    f.data.getDate() === d.getDate() && 
    f.data.getMonth() === d.getMonth()
  )
}

/**
 * Helper: verifica se una data è il santo patrono
 */
function checkPatrono(d, settings) {
  const patrono = settings.santoPatrono
  if (!patrono?.enabled || !patrono.giorno || !patrono.mese) return null
  
  if (d.getDate() === patrono.giorno && d.getMonth() + 1 === patrono.mese) {
    return {
      nome: patrono.nome || 'Santo Patrono',
      emoji: '⛪'
    }
  }
  return null
}

/**
 * Hook per gestire chiusure calendario
 */
export default function useCalendarioChiusure(calendarioSettings = {}) {
  /**
   * Check stato di un giorno (memoizzato)
   */
  const checkGiorno = useCallback((data) => {
    return getStatoGiorno(data, calendarioSettings)
  }, [calendarioSettings])
  
  /**
   * Ottiene lo stato di tutti i giorni di un mese
   */
  const getStatoMese = useCallback((anno, mese) => {
    const result = {}
    const primoGiorno = new Date(anno, mese - 1, 1)
    const ultimoGiorno = new Date(anno, mese, 0)
    
    const current = new Date(primoGiorno)
    while (current <= ultimoGiorno) {
      const dateKey = current.toISOString().split('T')[0]
      result[dateKey] = getStatoGiorno(current, calendarioSettings)
      current.setDate(current.getDate() + 1)
    }
    
    return result
  }, [calendarioSettings])
  
  /**
   * Verifica se un giorno è aperto
   */
  const isGiornoAperto = useCallback((data) => {
    const stato = getStatoGiorno(data, calendarioSettings)
    return stato.aperto
  }, [calendarioSettings])
  
  /**
   * Verifica se è una chiusura parziale
   */
  const isChiusuraParziale = useCallback((data) => {
    const stato = getStatoGiorno(data, calendarioSettings)
    return stato.parziale != null
  }, [calendarioSettings])
  
  /**
   * Verifica se è un festivo lavorato
   */
  const isFestivoLavorato = useCallback((data) => {
    const stato = getStatoGiorno(data, calendarioSettings)
    return ['festivita_lavorata', 'patrono_lavorativo', 'apertura_straordinaria'].includes(stato.tipologia)
  }, [calendarioSettings])
  
  /**
   * Ottiene le prossime chiusure
   */
  const getProssimeChiusure = useCallback((giorni = 30) => {
    const chiusure = []
    const oggi = new Date()
    oggi.setHours(0, 0, 0, 0)
    
    for (let i = 0; i <= giorni; i++) {
      const data = new Date(oggi)
      data.setDate(data.getDate() + i)
      
      const stato = getStatoGiorno(data, calendarioSettings)
      
      if (!stato.aperto && stato.tipologia !== 'weekend') {
        chiusure.push({
          data: data.toISOString(),
          dataFormattata: data.toLocaleDateString('it-IT', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
          }),
          giorniMancanti: i,
          ...stato
        })
      }
      
      // Max 10 chiusure
      if (chiusure.length >= 10) break
    }
    
    return chiusure
  }, [calendarioSettings])
  
  /**
   * Conta giorni lavorativi tra due date
   */
  const contaGiorniLavorativi = useCallback((startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    start.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)
    
    let giorniLavorativi = 0
    let giorniChiusura = 0
    
    const current = new Date(start)
    
    while (current <= end) {
      const stato = getStatoGiorno(current, calendarioSettings)
      
      if (stato.aperto) {
        giorniLavorativi++
      } else {
        giorniChiusura++
      }
      
      current.setDate(current.getDate() + 1)
    }
    
    return { giorniLavorativi, giorniChiusura }
  }, [calendarioSettings])
  
  return {
    checkGiorno,
    getStatoMese,
    isGiornoAperto,
    isChiusuraParziale,
    isFestivoLavorato,
    getProssimeChiusure,
    contaGiorniLavorativi
  }
}
